import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import PaiButton from 'features/ui/base-components/pai-button';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

interface Props {
  open: boolean;
  resetLink: string;
  userName: string;
  handleClose(): void;
}

const PasswordLinkDialog: React.FC<Props> = ({ open, resetLink, userName, handleClose }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(resetLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = resetLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('passwordSetLink', 'Password Set Link')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography className={classes.text}>
            {t(
              'passwordSetLinkDescription',
              'The user {{userName}} has been created successfully. You can share the following link so they can set their password. The link is valid for 24 hours.',
              { userName }
            )}
          </Typography>
          <TextField
            fullWidth
            value={resetLink}
            slotProps={{
              input: {
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleCopy} edge="end" title={t('copyToClipboard', 'Copy to clipboard')}>
                      {copied ? <CheckIcon color="success" /> : <ContentCopyIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
            onClick={(e) => (e.target as HTMLInputElement).select?.()}
            size="small"
          />
          {copied && (
            <Typography color="success.main" variant="body2">
              {t('copiedToClipboard', 'Copied to clipboard!')}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiButton text={t('close', 'Close')} size={'small'} border={'rounded'} action={handleClose} />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  text: {
    fontSize: theme.typography.pxToRem(14),
    whiteSpace: 'pre-wrap',
    marginBottom: theme.spacing(1),
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
}));

export default PasswordLinkDialog;
